/**
 * API rows → the shapes the broker Board draws.
 *
 * Same job, and the same discipline, as carrier/fromApi.js. Board.jsx is 1,960 lines written
 * against five hand-made arrays, and it reads a richer object than the database stores: a
 * carrier's insurance certificates, an on-time percentage, a live tracking ping. None of
 * that exists, and none of it is invented here.
 *
 * ⛔ WHAT THE SEEDS CLAIMED, AND WHAT WE ACTUALLY KNOW.
 *
 * The seed carriers carried a complete fraud dossier: "authority 4 years", "0% out of
 * service", "no changes in 18 months", "landline, single address", and an insurance block
 * with auto/cargo/GL limits and a cancellation date. They were written to make a screenshot
 * convincing. Our FMCSA path returns authorized / status / carrierName / mcNumber /
 * dotNumber / safetyRating — and much of it is still mock.
 *
 * That gap matters more here than anywhere else in this app. A broker looks at those fields
 * to decide whether a stranger claiming to be a carrier is real, and double-brokering is the
 * loss this product exists to prevent. An invented "0% out of service" is not a cosmetic
 * placeholder; it is the screen telling a broker a carrier is safe because a developer
 * needed a demo to look good. Absent reads as absent, and the Board must render it that way.
 *
 * ⛔ AND THE SAME FOR MONEY. The backend sends cents as integers plus a formatted string and
 * a margin that is null unless BOTH sides are known. Nothing here recomputes money from the
 * display strings — a margin parsed back out of "$3,400.00" is a margin that can be wrong.
 */

// The eight checks the brokerage screens were drawn around, paired with the `vetting` jsonb key
// each one reads. Nothing writes most of them yet; that is the point of rendering them as
// unchecked rather than leaving them out.
// ⛔ TRUE DOES NOT ALWAYS MEAN GOOD, AND THE FIRST VERSION ASSUMED IT DID.
//
// `authorized: true` is a pass. But the natural meaning of `recordChanged: true` is *the FMCSA
// record changed* — a fraud signal — and of `doNotUse: true` is *this carrier is on the
// do-not-use list*. Mapping both to a green ✓ is the same error as the CheckMark fallback,
// one layer down: an adapter painting a pass onto bad news. The third column is which boolean
// means "fine", so a key whose true is a warning flags instead.
const VET_CHECKS = [
  ['authorized', 'Authority', true],
  ['safety', 'Safety and OOS', true],
  ['insurance', 'Insurance', true],
  ['fmcsaCallback', 'FMCSA callback', true],
  // ⛔ true = the record CHANGED = a flag
  ['recordChanged', 'Record changes', false],
  ['phoneAddress', 'Phone and address', true],
  ['packet', 'Packet and W-9', true],
  // ⛔ true = they ARE on the do-not-use list = a flag
  ['doNotUse', 'Do-not-use', false],
];

/**
 * One check's state: 'pass' | 'warn' | 'flag' | 'none'.
 *
 * ⛔ ABSENT IS 'none', NEVER 'pass'. A string is taken as-is only when it is one of the three
 * real states, so a typo cannot become a tick.
 *
 * ⛔ TWO STORED SHAPES. Rows written before the write-side whitelist hold a bare value
 * (`authorized: true`); rows written since hold `{ state, at, by, source }` so the screen can
 * tell a person's claim from a platform check. `normaliseVetEntry`/`vetEntryState` in
 * backend/src/lib.mjs are the source of truth for both; these are the browser's copy, pinned to
 * the same cases by broker/vetState.test.mjs — NOT by fromApi.test.mjs, which this used to name
 * and which pins none of it.
 */
function rawState(v) {
  if (v === true || v === false) return v;
  if (typeof v === 'string') return (v === 'pass' || v === 'warn' || v === 'flag') ? v : null;
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const st = v.state;
    if (st === true || st === false) return st;
    if (typeof st === 'string' && (st === 'pass' || st === 'warn' || st === 'flag')) return st;
  }
  return null;
}
function vetState(vetting, key, trueIsGood = true) {
  const v = (vetting && typeof vetting === 'object' && !Array.isArray(vetting)) ? vetting[key] : undefined;
  const st = rawState(v);
  if (st === true) return trueIsGood ? 'pass' : 'flag';
  if (st === false) return trueIsGood ? 'flag' : 'pass';
  if (st === 'pass' || st === 'warn' || st === 'flag') return st;
  return 'none';
}
/**
 * Who made the claim: 'operator' | 'platform' | null.
 *
 * ⛔ NOTHING IN THIS PLATFORM CHECKS ANY OF THE EIGHT TODAY — `/api/fmcsa/lookup` returns a
 * result to the client and writes nothing to `vetting`. So every recorded entry is a PERSON
 * saying so, and the screen has to draw that differently from a verification. A tick a broker
 * put there themselves, shown the same as one the platform earned, is the whole defect this
 * column exists to prevent.
 */
// ⛔ THE SAME LIST THE SERVER KEEPS. This hardcoded `=== 'platform' ? 'platform' : 'operator'`,
// so a third source added to VETTING_SOURCES server-side would read as 'operator' here and the
// agreement test would still pass — proven by review with a 'fmcsa' value. The errs-safe
// direction (a real check drawn with a "said" chip understates it), but silent divergence is
// what that test exists to stop. Kept in step by broker/vetState.test.mjs.
// Exported so broker/vetState.test.mjs can compare it against the server's VETTING_SOURCES
// directly. Asserting today's two values is not enough: with both lists two long, hardcoding
// `=== 'platform'` behaves identically, so a sabotage of it passes — the review proved the gap
// with a third value and the test could not see it. Comparing the LISTS is what catches
// somebody adding a source to one side.
export const VET_SOURCES = ['operator', 'platform'];
function vetSource(vetting, key) {
  const v = (vetting && typeof vetting === 'object' && !Array.isArray(vetting)) ? vetting[key] : undefined;
  if (rawState(v) === null) return null;
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    return VET_SOURCES.includes(v.source) ? v.source : 'operator';
  }
  return 'operator';   // a legacy bare value was always somebody typing
}

/** A carrier in the brokerage's book. */
export function brokerCarrierFromApi(c) {
  if (!c) return null;
  return {
    id: c.id,
    name: c.name,
    // The Board calls these `mc` and `contact`; the API is explicit. Rename once, here.
    mc: c.mcNumber || null,
    dot: c.dotNumber || null,
    contact: c.contactName || null,
    phone: c.phone || null,
    // ⛔ THE REGISTERED NUMBER, SEPARATE. Calling the line on the FMCSA record rather than
    // the one in the email is the carrier-identity check; the backend keeps them apart and
    // so does this.
    fmcsaPhone: c.fmcsaPhone || null,
    status: c.status,
    bookable: !!c.bookable,
    // ⛔ WHY THE STATUS IS WHAT IT IS, AND WHO DECIDED. The board's own `audit` list is React
    // state that dies on refresh; these three are stored on the row. The profile screen tells
    // the operator a reason "goes in the audit trail", and this is the trail — so dropping
    // them here would leave that promise true in the database and invisible on the screen.
    statusReason: c.statusReason || null,
    statusBy: c.statusBy || null,
    statusAt: c.statusAt || null,
    vetting: c.vetting || {},

    // ⛔ THE VETTING CHECKLIST, DERIVED — AND "NOT CHECKED" IS A STATE.
    //
    // VetChecks maps `carrier.checks` as [label, state] pairs and the database has no such
    // column: it has a `vetting` jsonb holding whatever was actually recorded. The seeds carried
    // all eight as "pass" on every carrier, which is why the screen looked finished.
    //
    // The eight are listed so a broker can see what has NOT been done — an absent check is the
    // information, and dropping it would leave a short all-green list that reads as a clean
    // carrier. `none` is the state for one nobody has run, and CheckMark draws it as a dashed
    // question mark precisely because its old fallback was a tick.
    // [label, state, source] — the third element is what lets the screen say "attested by a
    // person" rather than drawing a person's claim as a verification.
    checks: VET_CHECKS.map(([key, label, good]) => [label, vetState(c.vetting, key, good), vetSource(c.vetting, key)]),
    // `anyPlatformCheck` was here and nothing read it — VetChecks counts for itself. A field
    // emitted and never consumed is a field a test can guard forever while protecting nothing,
    // which is a pattern this file's sibling test already calls out. Removed rather than wired.
    // `vettedAt` is the OLDEST recorded check — how stale this carrier's vetting is, which is
    // the fraud-relevant number. `vettingTouchedAt` is the last write. Neither is drawn yet;
    // whichever screen draws one has to say which it means.
    vettedAt: c.vettedAt || null,
    vettingTouchedAt: c.vettingTouchedAt || null,
    notes: c.notes || null,

    // ⛔ NOT INVENTED — see the note above. Every one of these was in the seed data and is
    // not in the database. The Board renders "not checked" for them rather than a number a
    // broker would act on.
    authorityAge: null,     // how long the authority has been active
    safety: null,           // FMCSA safety rating
    oos: null,              // out-of-service percentage
    recordChanged: null,    // when the FMCSA record last changed — a real fraud signal
    voip: null,             // whether the bid number is VOIP
    ins: null,              // insurance certificates, limits, cancellation
    onTime: null,
    detentionFree: null,
    // ⛔ THESE MUST BE PRESENT AND null, NOT ABSENT. Board.jsx guards the tracking panel and the
    // fleet metric on `carrier.tracking !== null`, and an OMITTED key satisfies that — so the
    // "no tracking data for any carrier" branch never ran, every real carrier showed a
    // "Tracking compliance —" row with an empty bar, and the fleet metric was subtitled
    // "Carriers who've run for us" while counting carriers that never have. An adapter whose
    // whole purpose is that absent reads as absent has to write the absence down.
    tracking: null,         // no visibility provider is wired to this app
    lanes: null,            // the lanes they run — not stored
    equipment: null,        // their equipment list — not stored
    answerRate: null,
    avgAnswerSec: null,
    loadsWithUs: null,
    // `note` is what the screen reads in two places; `notes` is what the server sends. Both,
    // so neither spelling silently hides the one real free-text field on a carrier.
    note: c.notes || null,
  };
}

/** A shipper the brokerage sells to. */
export function brokerCustomerFromApi(k) {
  if (!k) return null;
  return {
    id: k.id,
    name: k.name,
    contact: k.contactName || null,
    phone: k.phone || null,
    email: k.email || null,
    branch: k.branch || null,
    notes: k.notes || null,
    // `note` is what the screen reads in two places; `notes` is what the server sends. Both,
    // so neither spelling silently hides the one real free-text field on a customer.
    note: k.notes || null,

    // ⛔ PRESENT AND null, NOT ABSENT — and this adapter was missing all seven.
    //
    // The seeds carried them and the `broker_customers` table does not: it has name, contact,
    // phone, email, branch and notes, full stop. An OMITTED key is not the same as a null one
    // here, because the screen reads these directly:
    //
    //   UPDATE_VIA[c.updateVia]            → undefined → the word "undefined" in a chip
    //   `${c.detentionFree} minutes`       → "undefined minutes"
    //   rows.filter((c) => !c.doNotCall)   → counts EVERY customer as taking phone calls
    //   rows.filter((c) => c.detentionFree !== 120) → counts every one as non-standard
    //
    // The third is the one that matters: the screen's own words are that calling a customer who
    // mandates a portal "is a service failure", and an omitted field told the broker all of
    // them were happy to be called. That is the same class as the carrier side's invented
    // safety rating — a claim manufactured by an absence.
    // ⛔ THE TABLE STORES THESE NOW, so they come from the server instead of being hard-nulled
    // here. They are still `null` when nobody has answered the question — which is what the
    // columns are nullable for, and the reason `?? null` is used below rather than `|| null`:
    // `0` free time and `false` on do-not-call are ANSWERS, and `||` would erase both.
    updateVia: k.updateVia ?? null,        // call / portal / edi / email
    portal: k.portal ?? null,              // the portal address or EDI identifier
    detentionFree: k.detentionFree ?? null, // free time before detention starts, in minutes
    terms: k.terms ?? null,                // payment terms and paperwork requirements
    // ⛔ null, NOT false. `false` is a positive claim that they take phone calls; it is only
    // true once somebody has recorded it. The screens read all three states.
    doNotCall: k.doNotCall ?? null,

    // ⛔ A PHRASE, NOT A MONEY STRING. The screen puts this mid-sentence —
    // `${m - freeMins} minutes past — ${customer.accessorial.toLowerCase()}.` — so handing it
    // the server's "$150.00" renders "42 minutes past — $150.00." The authority is a number in
    // `accessorialCents`; this is the sentence built from it.
    //
    // ⛔ AND ZERO IS A REAL TERM, not an absence: "nothing without written approval" is the
    // strictest account on the file, and `|| null` would have shown it as unrecorded.
    accessorialCents: k.accessorialCents ?? null,
    accessorial: k.accessorialCents == null ? null
      : (k.accessorialCents === 0
        ? "Nothing without written approval"
        // Whole dollars when it is a whole dollar amount — "$150.00" reads as an invoice line,
        // not as an authority limit. The server's formatted string covers the odd-cents case.
        // ⛔ 'en-US' PINNED, as `fromCents` pins it on the server. Unpinned,
        // `(1500).toLocaleString()` is "1.500" in a de-DE browser — read as $1.50 by an
        // American, and this is the only money format in the path that was not pinned.
        : `Up to ${k.accessorialCents % 100 === 0
          ? `$${(k.accessorialCents / 100).toLocaleString('en-US')}`
          : (k.accessorial || `$${(k.accessorialCents / 100).toFixed(2)}`)} without approval`),
  };
}

/**
 * A load on the board.
 *
 * `buildPlan` is passed in rather than imported, because Board.jsx owns it and importing
 * Board from here would be a cycle — the same reason carrier/fromApi.js took the plan
 * builder in instead of reaching for it.
 *
 * ⛔ IT TAKES THE WHOLE LOAD, NOT THE STOPS. The carrier board's builder takes a stop list and
 * this one does not: it reads `load.freight.equipment` to decide what the loaded-at step should
 * capture, then walks `load.stops`. Calling it with an array threw
 * "Cannot read properties of undefined (reading 'equipment')" on the first real row — and
 * nothing caught it, because the adapter's unit tests pass no builder at all and the screen
 * never called the adapter. Found by rendering the two together for the first time.
 *
 * ⛔ AND THE SCREEN'S MONEY IS DOLLARS AS NUMBERS. `ceiling.toLocaleString()`,
 * `Number(ceil) === load.ceiling`, `v >= load.sell` — the seeds held `sell: 3400`. Handing it
 * the formatted strings made `marginOf` do `"$3,400.00" - "$2,900.00"` = NaN and
 * `load.ceiling.toLocaleString` throw. The cents stay authoritative and are what the margin is
 * read from; these are derived FROM the cents, never parsed from the display text.
 */
export function brokerLoadFromApi(l, { buildPlan } = {}) {
  if (!l) return null;
  const stops = Array.isArray(l.stops) ? l.stops : [];
  // ⛔ THE API SPREADS `detail` AT THE TOP LEVEL — IT DOES NOT SEND A `detail` KEY.
  //
  // `brokerLoadOut` spreads the jsonb into the response and then re-sets every real column over
  // it, deliberately, so nothing a client wrote into `detail` can shadow a server-computed
  // value. This read looked for a nested `detail` the wire has never carried, so `freight`,
  // `paperwork`, `activity` and `plan` were EMPTY for every real row. Measured, through
  // `brokerLoadOut`, against six rows in a database:
  //
  //   db detail.freight {"equipment":"reefer","commodity":"Mixed produce",…}
  //   api.freight       {"equipment":"reefer","commodity":"Mixed produce",…}   ← top level
  //   ui.freight        {}                                                     ← lost here
  //   inScope           "undefined is outside Alfred's scope"
  //
  // So: no equipment, commodity, weight or temperature on any load; `buildPlan` falling back to
  // dry-van captures on a reefer, which means Alfred would never ask the set point, the pre-cool
  // or the pulp temp; and the coverage board printing the literal word "undefined" as the reason
  // a load is out of scope — the same defect `inScope`'s branch check one line above it had
  // already been fixed for.
  //
  // ⛔ AND THE SUITE WAS GREEN THROUGHOUT. Every fixture in fromApi.test.mjs carries a nested
  // `detail`, a shape production never sends, and every assertion about `freight` tests its TYPE
  // — `{}` is an object — and never its content. A test that cannot fail is worse than no test.
  //
  // ⛔ SO IT IS A WHITELIST OF FOUR KEYS, AND NOT A SPREAD OF THE ROW.
  //
  // The first version of this fix made `detail` fall back to `l` itself and kept `...detail`,
  // which spread the WHOLE API row. That fixed the four keys and opened everything else in an
  // unvalidated, client-writable jsonb — `upsertBrokerLoad` merges the request body with no
  // whitelist — straight onto the board. An independent review measured two that matter:
  //
  //   detail: { bids: [{carrierId, at, note}] }  → a "Bids · N worked" disclosure, and
  //                                                "N bids" under every uncovered load
  //   detail: { rep: 'u5', second: 'u3' }        → named people on the escalation ladder
  //                                                (`routeRep`, `ladderFor`, `myLoadsOnly`)
  //
  // Those are the two things the demo seed's own header refuses to write, for the reason
  // eleven commits were spent on: a worked bid list is a claim that carriers called in, and a
  // seat id writes a person into an audit trail for work they never did. Before the fix
  // `detail` was `{}` on every real row, so the surface was zero; the spread took it to
  // everything.
  //
  // ⛔ AND THE WHOLE-OBJECT BRANCH SELECTOR WAS ATTACKER-CHOSEN. `detail: { detail: {...} }`
  // put an object at `l.detail`, so the function took the nested branch and read the PLANTED
  // object instead of the real one. Measured: real jsonb said `reefer`, the planted inner said
  // `flatbed`, the adapter returned flatbed. Reading each key individually removes the branch.
  //
  // So: no spread, four keys, each nested-or-flat. The adapter now emits exactly the fields it
  // names, which is the "whitelist" both this file and `brokerLoadOut` called the real fix.
  // ⛔ FLAT WINS, AND THE ORDER IS THE SECURITY PROPERTY — not a style choice.
  //
  // Preferring the nested value left the attack intact: `detail: { freight: {reefer},
  // detail: { freight: {flatbed} } }` is spread by `brokerLoadOut` into a response carrying BOTH
  // `freight` (the real one) and `detail` (the planted one), and a nested-first read returned
  // flatbed. Measured, twice — the branch selector moved but the attacker still chose it.
  //
  // `brokerLoadOut` spreads the whole jsonb, so on the wire every key is present at the top
  // level with its real value. The flat read is therefore always the authoritative one, and the
  // nested read is only the fallback for a raw-ish row that was never through the mapper.
  const nested = (l.detail && typeof l.detail === 'object' && !Array.isArray(l.detail)) ? l.detail : {};
  const jsonb = (k) => (l[k] !== undefined ? l[k] : nested[k]);
  return {
    id: l.id,
    ref: l.ref,
    status: l.status,
    branch: l.branch || null,
    customerId: l.customerId ?? null,
    carrierId: l.carrierId ?? null,

    // ⛔ MONEY COMES FROM THE SERVER, IN CENTS, AND IS NOT RECOMPUTED HERE.
    // `margin` is null unless both sides are known — an uncovered load has no buy rate, and
    // treating that as zero would report the whole sell price as profit on exactly the loads
    // nobody has covered yet.
    // ⛔ CENTS ARE THE TRUTH. Every comparison and every margin reads these.
    sellCents: l.sellCents ?? null,
    buyCents: l.buyCents ?? null,
    ceilingCents: l.ceilingCents ?? null,
    marginCents: l.marginCents ?? null,
    // Whole dollars, DERIVED from the cents, because that is the shape the screen's arithmetic
    // and `.toLocaleString()` calls were written against. A half-cent cannot survive this and
    // does not need to: a freight rate is whole dollars, and the cents remain for the margin.
    sell: l.sellCents == null ? null : Math.round(l.sellCents / 100),
    buy: l.buyCents == null ? null : Math.round(l.buyCents / 100),
    ceiling: l.ceilingCents == null ? null : Math.round(l.ceilingCents / 100),
    // ⛔ `margin` TOO, even though the board reads the margin through `marginOf`. The wire carries
    // the server's FORMATTED string under this name, and now that `detail` falls back to the row
    // itself that string survives the spread — under an arithmetic-shaped name, beside three
    // siblings that are numbers. Measured: `ui.margin` was "$850.00" while `ui.sell` was 4200.
    // Nothing in Board.jsx reads a bare `.margin` today, so this is a trap rather than a live
    // bug — and it is exactly the trap the note below describes, which already cost a NaN margin
    // and a `.toLocaleString()` on a string once.
    margin: l.marginCents == null ? null : Math.round(l.marginCents / 100),
    // The server's formatted strings, kept under names nothing can do arithmetic on by mistake.
    sellText: l.sell ?? null,
    buyText: l.buy ?? null,
    ceilingText: l.ceiling ?? null,
    marginText: l.margin ?? null,
    overCeiling: !!l.overCeiling,

    stops,

    // ⛔ ALWAYS ARRAYS/OBJECTS, because the Board reads them unguarded — `plan` 12 times,
    // `freight` 9, `activity` 8, `paperwork` 3. On the carrier side the identical gap was a
    // white screen on the first real row, and `vite build` was green throughout.
    freight: jsonb('freight') || {},
    paperwork: Array.isArray(jsonb('paperwork')) ? jsonb('paperwork') : [],
    activity: Array.isArray(jsonb('activity')) ? jsonb('activity') : [],

    // ⛔ NO TRACKING INTEGRATION EXISTS. The seeds showed "MacroPoint, 6 min ago" beside a
    // green tick. Nothing in this stack talks to a visibility provider, so a dispatcher
    // reading a fresh ping would be reading a developer's placeholder.
    tracking: null,
    trackingOk: false,
    lastPing: null,
    // ⛔ EXPLICIT null, NOT OMITTED. Board.jsx guards on `carrier.tracking !== null`, and an
    // ABSENT key makes that true — so the "no tracking data" branch never ran and every real
    // carrier rendered a "Tracking compliance —" row with an empty bar, plus a fleet metric
    // subtitled "Carriers who've run for us" counting carriers that never have.
    // An adapter that promises absent-reads-as-absent has to write the absence down.

    // Derived from the real stops, like the carrier board's. Only the first step is due:
    // we do not know what has already been done, and ticking steps off would show calls
    // nobody made.
    // ⛔ THE BUILDER TAKES THE LOAD. See the note above. It is handed the fields it actually
    // reads — `freight` and `stops` — rather than the half-built object this function is in
    // the middle of returning.
    plan: typeof buildPlan === 'function'
      ? buildPlan({ ...l, stops, freight: jsonb('freight') || {} })
          .map((s, i) => ({ ...s, state: s.state === 'scheduled' && i === 0 ? 'due' : s.state }))
      : (Array.isArray(jsonb('plan')) ? jsonb('plan') : []),

    createdAt: l.createdAt || null,
    updatedAt: l.updatedAt || null,
  };
}

export const brokerCarriersFromApi = (rows) => (rows || []).map(brokerCarrierFromApi).filter(Boolean);
export const brokerCustomersFromApi = (rows) => (rows || []).map(brokerCustomerFromApi).filter(Boolean);
export const brokerLoadsFromApi = (rows, opts) => (rows || []).map((l) => brokerLoadFromApi(l, opts)).filter(Boolean);
